import { present, defined, definedNotNull } from 'shared/presenters/presenter'
import { translatablePresenter } from 'shared/presenters/translatablePresenter'
import { Schedulables } from 'shared/presenters/graphqlTypes'
import determineImageFromSchedulableImages, {
  DetermineImageFromSchedulableProps
} from 'shared/helpers/determineImageFromSchedulable'
import { DEFAULT_FALLBACK_IMAGE } from 'enums/image'
import formatDurationText from 'shared/helpers/formatDurationText'
import { Translate } from 'next-translate'
import dayjs from 'dayjs'
import { DateRange } from 'react-day-picker'
import customParseFormat from 'dayjs/plugin/customParseFormat'

enum ParticipationModel {
  mixed = 'mixed',
  children_only = 'children_only',
  adults_only = 'adults_only'
}

enum PricingModelNew {
  flat = 'flat',
  base_plus = 'base_plus',
  per_person = 'per_person'
}

dayjs.extend(customParseFormat)

export class SchedulablePresenter extends translatablePresenter(
  present<Schedulables>()
) {
  name = () => this.translateField('name')
  description = () => this.translateField('description')
  whatToBring = () => this.translateField('what_to_bring')
  itinerary = () => this.translateField('itinerary')
  requirements = () => this.translateField('requirements')
  tagline = () => this.translateField('tagline')
  highlightsAi = () => {
    const highlights_ai_text = this.translateField('highlights_ai_text')
    return highlights_ai_text?.replace(/\n/g, '<br/>')
  }

  isRental = () => this.type === 'Rental'
  isAccommodation = () => this.type === 'Accommodation'

  participationModel = () =>
    this.participation_model as unknown as ParticipationModel
  pricingModel = () => this.pricing_model_new as unknown as PricingModelNew

  childrenAllowed = () => {
    return [
      ParticipationModel.mixed,
      ParticipationModel.children_only
    ].includes(this.participationModel())
  }

  adultsAllowed = () => {
    return [ParticipationModel.mixed, ParticipationModel.adults_only].includes(
      this.participationModel()
    )
  }

  isMixed = () => this.participationModel() === ParticipationModel.mixed
  isChildrenOnly = () =>
    this.participationModel() === ParticipationModel.children_only
  isAdultsOnly = () =>
    this.participationModel() === ParticipationModel.adults_only

  usesBasePrice = () => {
    return [PricingModelNew.flat, PricingModelNew.base_plus].includes(
      this.pricingModel()
    )
  }

  hasPeopleIncluded = () => {
    return (
      this.pricingModel() === PricingModelNew.base_plus &&
      !!this.people_included &&
      this.people_included > 0
    )
  }

  /**
   * Starting price of the activity which is the
   * min of adult/child price or whichever is defined.
   * Allows for £0 say if the activity is free for children when accompanied.
   */
  priceStartingFrom = () => {
    const basePricePence = defined(this.base_price_pence)

    if (this.usesBasePrice()) {
      return basePricePence
    } else {
      return this.perPersonPriceStartingFrom()
    }
  }

  perPersonPriceStartingFrom = () => {
    const pricePence = defined(this.adult_price_pence)
    const childPricePence = defined(this.child_price_pence)
    const hasAdults = this.adultsAllowed() && pricePence !== null
    const hasChildren = this.childrenAllowed() && childPricePence !== null

    if (hasAdults && hasChildren) {
      return Math.min(pricePence, childPricePence) // allows for £0
    } else if (hasAdults) {
      return pricePence
    } else if (hasChildren) {
      return childPricePence
    } else {
      throw 'All fetched prices are null'
    }
  }

  accommodationPriceStartingFrom = () => {
    const basePricePence = defined(this.base_price_pence)
    const pricePence =
      this.price_points && this.price_points.length > 0
        ? Math.min(
            ...this.price_points.map(
              pp =>
                defined(this.adult_price_pence) -
                (pp.price_reduction_pence || 0)
            )
          )
        : defined(this.adult_price_pence)

    const childPricePence =
      this.price_points && this.price_points.length > 0
        ? Math.min(
            ...this.price_points.map(
              pp =>
                defined(this.child_price_pence) -
                (pp.price_reduction_pence || 0)
            )
          )
        : defined(this.child_price_pence)

    const hasAdults = this.adultsAllowed() && pricePence !== null
    const hasChildren = this.childrenAllowed() && childPricePence !== null

    if (this.usesBasePrice()) {
      return basePricePence
    } else if (hasAdults && hasChildren) {
      return Math.min(pricePence, childPricePence) // allows for £0
    } else if (hasAdults) {
      return pricePence
    } else if (hasChildren) {
      return childPricePence
    } else {
      throw 'All fetched prices are null'
    }
  }

  /**
   * Calculates total price for attendees.
   * An example of where this is used is in the Summary component for the BookingConfig
   * view.
   */
  totalPriceForAttendees = ({
    adults = 0,
    children = 0
  }: {
    adults?: number
    children?: number
  }) => {
    let totalPrice = 0

    const basePricePence = defined(this.base_price_pence)
    const pricePence = defined(this.adult_price_pence)
    const childPricePence = defined(this.child_price_pence)
    const peopleGoing = adults + children

    if (
      basePricePence == null &&
      pricePence == null &&
      childPricePence == null
    ) {
      throw 'All fetched prices are null.'
    }

    const pricingModel = this.pricingModel()

    if (pricingModel === PricingModelNew.flat) {
      return basePricePence
    }

    if (pricingModel === PricingModelNew.base_plus) {
      const peopleIncluded = definedNotNull(this.people_included)
      totalPrice += basePricePence
      if (peopleGoing <= peopleIncluded) {
        return totalPrice // no extra charge if within included people
      } else {
        // Calculate extra charges beyond peopleIncluded
        // Always optimize for minimum cost by counting the more expensive group towards peopleIncluded first

        let adultsToInclude, childrenToInclude

        if (pricePence >= childPricePence) {
          // Adults are more expensive or equal, count them first
          adultsToInclude = Math.min(adults, peopleIncluded)
          childrenToInclude = Math.min(
            children,
            peopleIncluded - adultsToInclude
          )
        } else {
          // Children are more expensive, count them first
          childrenToInclude = Math.min(children, peopleIncluded)
          adultsToInclude = Math.min(adults, peopleIncluded - childrenToInclude)
        }

        const extraAdults = Math.max(0, adults - adultsToInclude)
        const extraChildren = Math.max(0, children - childrenToInclude)

        totalPrice += extraAdults * pricePence + extraChildren * childPricePence

        return totalPrice
      }
    }

    if (children) {
      if (childPricePence === null) {
        throw 'Children specified but child_price_pence is null'
      }
      totalPrice += children * childPricePence
    }

    if (adults) {
      if (pricePence === null) {
        throw 'Adults specified but adults_price_pence is null'
      }
      totalPrice += adults * pricePence
    }

    return totalPrice
  }

  /**
   * Return the most specific image possible for this schedulable, resized.
   */
  imageURL = (
    transform: DetermineImageFromSchedulableProps['transform'] = 'chargeable'
  ) => {
    const imageUrl = determineImageFromSchedulableImages({
      image: this.image,
      defaultImage: this.default_image,
      activityType: this.activity_type,
      transform
    })
    if (imageUrl) {
      return imageUrl
    }

    return DEFAULT_FALLBACK_IMAGE
  }

  getDurationInHours = () => Math.floor(this.duration_mins ?? 0 / 60)

  /**
   * In the business app, duration is selected from a number of hours up to 12, and then multiples of days.
   * If durationMins is less than 720, then durationMins represents the length of activity.
   * If durationMins is greater than 720, then it represents the number of days the activity will run on.
   * For example, a duration of 4320 means the activity runs on 3 consecutive days.
   * The actual duration in minutes in this case is defined by the start and finish time chosen by the outlet.
   */
  getDurationInDays = () => {
    const durationMins = this.duration_mins ?? 0
    return durationMins > 720 ? Math.ceil(durationMins / 1440) : 0
  }

  getDurationInNights = () => this.getDurationInDays() - 1

  isMultiDay = () => this.getDurationInDays() > 0

  formatDurationText = (t: Translate, variant?: string) =>
    formatDurationText({
      durationInMins: this.duration_mins ? this.duration_mins : 0,
      continuous: this.continuous,
      variant,
      t
    })

  /**
   * Is the location determined on the day (no address given)?
   */
  locationOnTheDay = () => defined(this.address) == null

  /**
   * Are any group discounts available?
   */
  groupDiscountsAvailable = () => defined(this.price_points).length > 0

  /**
   * Are any rental duration discounts available?
   */
  durationDiscountsAvailable = () => {
    if (this.type !== 'Rental') return false

    return defined(this.resource_requirements).some(resourceReq => {
      return (
        resourceReq?.resource_price_points &&
        resourceReq?.resource_price_points?.length > 0
      )
    })
  }

  addressSummaryLine = (): string | undefined => {
    const city = this.address?.city
    const country = this.address?.country

    if (city && country) {
      return `${city}, ${country}`
    } else if (city || country) {
      return (city as string) ?? (country as string)
    } else {
      return undefined
    }
  }

  getCoordinates = () => {
    const latitude = this.address?.latitude
    const longitude = this.address?.longitude

    if (!latitude || !longitude) return

    return {
      latitude,
      longitude
    }
  }

  googleMapsImageURL = () => {
    const latitude = this.address?.latitude
    const longitude = this.address?.longitude

    if (!latitude || !longitude) return

    return [
      // GOOGLE_MAPS_IMAGE_PREFIX,
      'https://maps.googleapis.com/maps/api/staticmap?',
      'key=',
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
      '&visual_refresh=true',
      '&markers=icon:https://res.cloudinary.com/eola/image/upload/h_30/v1511334399/eola/public_images/gmaps-markerv1.png%7C',
      [latitude, longitude].join(','),
      '&size=',
      320,
      'x',
      240,
      '&scale=',
      2, // retina,
      '&zoom=',
      14
    ].join('')
  }

  googleMapsURL = () => {
    const latitude = this.address?.latitude
    const longitude = this.address?.longitude

    if (!latitude || !longitude) return

    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
  }

  hasDetails = () => {
    return (
      this?.description() ||
      this?.whatToBring() ||
      this?.itinerary() ||
      this?.requirements()
    )
  }

  lowestResourceReqPrice = (
    resourceReqs = defined(this.resource_requirements)
  ): number => {
    const resourceReqPrices = resourceReqs?.map(
      resourceReq => resourceReq?.price_pence
    )
    return resourceReqPrices && resourceReqPrices?.length > 0
      ? Math.min(...(resourceReqPrices as number[]))
      : 0
  }

  hasEnoughPeopleGoingForExclusive = (peopleGoing: number): boolean => {
    if (this.allow_exclusive_bookings) {
      return (
        peopleGoing >= definedNotNull(this.minimum_party_size_for_exclusive)
      )
    } else {
      return true
    }
  }

  /**
   * This method is used to determine whether the schedulable is bookable this weekend.
   * WARN: This method requires specific query data to be returned to be valid.
   * WARN: time_slot_bookings_aggregate must be filtered to weekend time slots.
   */
  availableThisWeekend = () => {
    const saturday = dayjs().day(6).format('YYYY-MM-DD')
    const sunday = dayjs().day(7).format('YYYY-MM-DD')
    return (
      definedNotNull(this.materialized_bookability_by_days).filter(
        bookability => {
          return (
            bookability?.on_date == saturday || bookability?.on_date == sunday
          )
        }
      ).length > 0
    )
  }

  /**
   * Counts total availability remaining, if less than 10 spots left, return true.
   */
  fewSpotsLeft = () => {
    return (
      definedNotNull(this.materialized_bookability_by_days).reduce(
        (partialSum, bookability) =>
          partialSum + (bookability?.max_availability || 0),
        0
      ) <= 10
    )
  }

  /**
   * Counts spaces availabile for a given date range, if less than 3 spaces left, return true.
   */
  fewSpacesLeft = (dateRange?: DateRange) => {
    if (!dateRange?.from || !dateRange?.to) return false

    return this.availableSpacesForRange(dateRange).length < 3
  }

  /**
   * Returns ids of spaces with availability for a given date range.
   * As availability can be split into multiple windows, it checks that there is at least one
   * availability window for each space that covers each night in the range.
   * It does this by iterating through the range and checking that a window exists
   * that begins before or on the day we are iterating on and ends after or on the next day.
   * If there is a window for every night of the range for a space then that space has availability.
   */
  availableSpacesForRange = (dateRange?: DateRange) => {
    const availabilities = definedNotNull(this.tarp_availabilities)
    const possibleSpaces: string[] = availabilities.map(avail =>
      definedNotNull(avail?.space_id)
    )
    if (!dateRange?.from || !dateRange?.to) return possibleSpaces

    const startDate = dayjs(dateRange?.from).startOf('day')
    const endDate = dayjs(dateRange?.to).startOf('day')
    const uniqSpaceIds = [...new Set(possibleSpaces)]

    return uniqSpaceIds.filter(spaceId => {
      const spacesAvailabilities = availabilities.filter(
        availability => availability?.space_id === spaceId
      )
      const nightHasValidWindow: boolean[] = []

      let currentDay = startDate
      while (currentDay.isBefore(endDate)) {
        const nextDay = currentDay.add(1, 'day')

        nightHasValidWindow.push(
          spacesAvailabilities.some(avail => {
            const start = dayjs(definedNotNull(avail?.start)).startOf('day')
            const finish = dayjs(definedNotNull(avail?.finish)).startOf('day')

            return (
              (start.isSame(currentDay) || start.isBefore(currentDay)) &&
              (finish.isAfter(nextDay) || finish.isSame(nextDay))
            )
          })
        )
        currentDay = nextDay
      }

      return nightHasValidWindow.every(nightValid => nightValid)
    })
  }

  // Returns true if there are any approval requirements for this schedulable.
  requiresApproval = () => {
    return (this?.approval_requirements_aggregate?.aggregate?.count ?? 0) > 0
  }

  tags = (t: Translate) => {
    return [
      this.isRental() ? t('common:rental') : '',
      this.isRental() ? '' : this.formatDurationText(t, 'short'),
      this.isRental()
        ? ''
        : t('maximum-people', {
            count: definedNotNull(this.maximum_capacity)
          }),
      this.members_only ? t('common:members-only') : '',
      this.groupDiscountsAvailable() ? t('common:group-discounts') : '',
      this.durationDiscountsAvailable() ? t('common:duration-discounts') : ''
    ].filter(x => x !== '')
  }

  labels = ({
    t,
    showUpsellLabels
  }: {
    t: Translate
    showUpsellLabels: boolean
  }) => {
    return [
      {
        title: 'available',
        active: this.availableThisWeekend() && showUpsellLabels,
        icon: 'ticket-star',
        text: t('common:available-this-weekend')
      },
      {
        title: 'popular',
        active: this.popularity ? this.popularity < 'Z' : false,
        icon: 'heart',
        text: t('common:popular')
      }
    ].filter(x => x.active)
  }

  formattedCheckInTime = () =>
    dayjs(definedNotNull(this?.check_in_time), 'HH:mm:ss').format('hh:mm a')

  formattedCheckOutTime = () =>
    dayjs(definedNotNull(this?.check_out_time), 'HH:mm:ss').format('hh:mm a')
}
